/**
 * Central location for all routes in the application
 * This file should be imported whenever a URL is needed
 */

import {
  HomeIcon,
  UsersIcon,
  MapPinIcon,
  CalendarIcon,
  SettingsIcon,
  BarChart3Icon,
  Target,
  ShoppingBag,
  Compass,
  ClipboardList,
  MessageSquare,
  Building,
  HelpCircle,
} from "lucide-react";

/**
 * Type for query parameters
 */
type QueryParams = Record<string, string | number | boolean | undefined>;

/**
 * Utility function to build a URL with query parameters
 */
function createUrl(baseUrl: string, params?: QueryParams): string {
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Routes object containing all application routes
 */
export const routes = {
  // Main routes
  home: "/",
  login: (params?: QueryParams) => createUrl("/login", params),
  register: "/register",
  forgotPassword: "/forgot-password",
  terms: "/terms",
  privacy: "/privacy",
  pricing: "/pricing",

  // Dashboard routes
  dashboard: {
    root: "/local-links-v1",
    stats: "/local-links-v1/stats",
    schedule: "/local-links-v1/schedule",
    settings: "/local-links-v1/settings",
    billing: "/local-links-v1/settings?tab=billing",
    notifications: "/local-links-v1/notifications",
    messages: (recipientId?: string, name?: string) => {
      if (!recipientId) return "/local-links-v1/messages";
      return createUrl("/local-links-v1/messages", {
        recipient: recipientId,
        name,
      });
    },
    clubSwap: {
      root: "/local-links-v1/club-swap",
      create: "/local-links-v1/club-swap/create",
      detail: (listingId: string) => `/local-links-v1/club-swap/${listingId}`,
      edit: (listingId: string) =>
        `/local-links-v1/club-swap/${listingId}/edit`,
      myListings: "/local-links-v1/club-swap/my-listings",
      saved: "/local-links-v1/club-swap/saved",
    },
    leagues: {
      root: "/local-links-v1/leagues",
      create: "/local-links-v1/leagues/create",
      detail: (leagueId: string) => `/local-links-v1/leagues/${leagueId}`,
      manage: (leagueId: string) =>
        `/local-links-v1/leagues/${leagueId}/manage`,
      messages: (leagueId: string) =>
        `/local-links-v1/leagues/${leagueId}/messages`,
      events: (leagueId: string) =>
        `/local-links-v1/leagues/${leagueId}/events`,
    },
    matchmaking: {
      root: "/local-links-v1/matchmaking",
      create: "/local-links-v1/matchmaking/create",
      detail: (matchId: string) => `/local-links-v1/matches/${matchId}`,
      solo: "/local-links-v1/matches/solo",
      discovery: "/local-links-v1/matchmaking",
    },
    quickMatch: {
      root: "/local-links-v1/matchmaking",
    },
    matches: {
      root: "/local-links-v1/matches",
      detail: (matchId: string) => `/local-links-v1/matches/${matchId}`,
      view: (matchId: string) => `/local-links-v1/matches/${matchId}`,
    },
    clubhouses: {
      root: "/local-links-v1/clubhouses",
      detail: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}`,
      settings: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}/settings`,
      connectStripe: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}/connect-stripe`,
      createLeague: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}/leagues/create`,
      createEvent: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}/events/create`,
      members: (clubhouseId: string) =>
        `/local-links-v1/clubhouses/${clubhouseId}/members`,
    },
    courses: {
      root: "/local-links-v1/courses",
      detail: (courseId: string) => `/local-links-v1/courses/${courseId}`,
    },
    feed: "/local-links-v1/feed",
    social: "/local-links-v1/social",
    records: "/local-links-v1/records", // ADD THIS
    subscribe: "/local-links-v1/subscribe",
    join: (params?: QueryParams) => createUrl("/join", params),
  },

  // League Manager routes (deprecated - redirects to /leagues?view=manageLeagues)
  leagueManager: {
    dashboard: "/local-links-v1/leagues?view=manageLeagues",
    // Legacy route - kept for backwards compatibility, redirects to new location
  },

  // API routes
  api: {
    auth: "/api/auth",
    createCheckoutSession: "/api/payments/checkout/general",
    createClubhouseCheckoutSession: "/api/payments/checkout/clubhouse",
    createCombinedCheckoutSession: "/api/payments/checkout/combined",
    createLeagueProduct: "/api/payments/products/league",
    createStripeConnectAccount: "/api/payments/connect/account",
    joinLeague: "/api/leagues/join",
    notifications: "/api/notifications",
    nearbyCourses: "/api/nearby-courses",
    places: "/api/places",
    matchmaking: "/api/matchmaking",
    // createLeagueEvent: replaced by POST /api/leagues/[leagueId]/events
    createLeagueManagerSubscription:
      "/api/payments/subscriptions/league-manager",
    createLeagueManagerStripeConnect: "/api/payments/connect/league-manager",
    disconnectLeagueManagerStripeConnect: "/api/payments/connect/disconnect",
    webhooks: "/api/webhooks",
    socket: "/api/socket",
    ai: "/api/ai",
    geocode: "/api/geocode",
  },

  // External links
  external: {
    stripeDashboard: "https://dashboard.stripe.com",
    stripeTerms: "https://stripe.com/terms",
    stripeLegal: "https://stripe.com/legal",
  },

  // Helper functions
  loginWithRedirect: (redirectPath: string) =>
    createUrl("/login", { redirect: redirectPath }),

  // Utility function to create full URLs with the app's base URL
  getFullUrl: (path: string, appBaseUrl?: string) => {
    const baseUrl = appBaseUrl || process.env.NEXT_PUBLIC_APP_URL || "";
    return new URL(path, baseUrl).toString();
  },
};

export const dashboardNav = [
  { href: "/local-links-v1", label: "Home", icon: HomeIcon },
  { href: "/local-links-v1/feed", label: "Feed", icon: Compass },
  {
    href: "/local-links-v1/matchmaking",
    label: "Lobby",
    icon: Target,
  },
  { href: "/local-links-v1/leagues", label: "Leagues", icon: UsersIcon },
  { href: "/local-links-v1/clubhouse", label: "Clubhouses", icon: Building },
  { href: "/local-links-v1/club-swap", label: "Club Swap", icon: ShoppingBag },
  { href: "/local-links-v1/schedule", label: "Schedule", icon: CalendarIcon },
  { href: "/local-links-v1/courses", label: "Courses", icon: MapPinIcon },
  { href: "/local-links-v1/records", label: "Records", icon: ClipboardList },
  { href: "/local-links-v1/stats", label: "Stats", icon: BarChart3Icon },
  { href: "/local-links-v1/social", label: "Social", icon: UsersIcon },
  { href: "/local-links-v1/messages", label: "Messages", icon: MessageSquare },
  { href: "/local-links-v1/settings", label: "Settings", icon: SettingsIcon },
  { href: "https://github.com/Colton923/local-links/blob/main/docs/README.md", label: "Docs", icon: HelpCircle },
];

export default routes;
